import { html, css, unsafeCSS } from "lit";
import { RdDropDownPanel } from "./DropDownPanel.js";

export class ColorPickerPanel extends RdDropDownPanel {
  static styles = [super.styles, css``];
  static properties = {
    swatches: { type: Array },
    value: { type: String, reflect: true },
    onColorChange: { type: Function },
  };

  connectedCallback() {
    super.connectedCallback();
    this._initColorPicker();
  }

  constructor() {
    super();
    this.isHtml = true;
    this.size = "auto";
    /**
     * @type {string[]}
     */
    this.swatches = window._globalColors || ["#264653", "#2a9d8f", "#e9c46a", "#e76f51", "#d62828", "#0096c7"];
    this._instance = null;
    this.value = "#1f0202";
    this.onColorChange = null;
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this._initColorPicker();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    this._initColorPicker();
  }

  /**
   * Add current color to swatches
   */
  _addCurrentColorToSwatches() {
    if (!this.value) return;

    // Check if color already exists in swatches
    if (!this.swatches.includes(this.value)) {
      this.swatches = [...this.swatches, this.value];

      // Update global colors if exists
      if (window._globalColors) {
        window._globalColors = this.swatches;
      }

      this.dispatchEvent(
        new CustomEvent("swatch-added", {
          detail: { color: this.value, swatches: this.swatches },
        }),
      );

      this.requestUpdate();
    }
  }

  /**
   * Remove a color from swatches
   */
  _removeSwatchColor(color, event) {
    event.stopPropagation();
    this.swatches = this.swatches.filter((c) => c !== color);

    // Update global colors if exists
    if (window._globalColors) {
      window._globalColors = this.swatches;
    }

    this.dispatchEvent(
      new CustomEvent("swatch-removed", {
        detail: { color, swatches: this.swatches },
      }),
    );

    this.requestUpdate();
  }

  /**
   * Select a swatch color
   */
  _selectSwatchColor(color) {
    const c = this.value;
    this.value = color;
    // Update the coloris picker
    const input = this.querySelector("#color_picker");
    if (input) {
      if (this.value !== c) {
        if (this.onColorChange && typeof this.onColorChange === "function") {
          Utilities.debounce(
            () => {
              this.onColorChange(this.value);
              input.dispatchEvent(new Event("input", { bubbles: true }));
              input.value = color;
            },
            50,
            "colorPicker",
          )();
        }
      }

      this._initColorPicker();
    }
  }

  _initColorPicker() {
    Coloris({
      parent: "#color_picker_content",
      el: "#color_picker",
      wrap: true,
      rtl: false,

      // Available themes: default, large, polaroid, pill (horizontal).
      // More themes might be added in the future.
      theme: "default",

      // Set the theme to light or dark mode:
      // * light: light mode (default).
      // * dark: dark mode.
      // * auto: automatically enables dark mode when the user prefers a dark color scheme.
      themeMode: "light",

      margin: 2,

      // Set the preferred color string format:
      // * hex: outputs #RRGGBB or #RRGGBBAA (default).
      // * rgb: outputs rgb(R, G, B) or rgba(R, G, B, A).
      // * hsl: outputs hsl(H, S, L) or hsla(H, S, L, A).
      // * auto: guesses the format from the active input field. Defaults to hex if it fails.
      // * mixed: outputs #RRGGBB when alpha is 1; otherwise rgba(R, G, B, A).
      format: "auto",

      // Set to true to enable format toggle buttons in the color picker dialog.
      // This will also force the format option (above) to auto.
      formatToggle: true,

      // Enable or disable alpha support.
      // When disabled, it will strip the alpha value from the existing color string in all formats.
      alpha: true,

      // Set to true to always include the alpha value in the color value even if the opacity is 100%.
      forceAlpha: false,

      // Set to true to hide all the color picker widgets (spectrum, hue, ...) except the swatches.
      swatchesOnly: false,

      // Focus the color value input when the color picker dialog is opened.
      focusInput: true,

      // Select and focus the color value input when the color picker dialog is opened.
      selectInput: false,

      // Show an optional clear button
      clearButton: false,

      // Set the label of the clear button
      clearLabel: "Clear",

      // Show an optional close button
      closeButton: false,

      // Set the label of the close button
      closeLabel: "Close",
      // swatches: ['#264653', '#2a9d8f', '#e9c46a', 'rgb(244,162,97)', '#e76f51', '#d62828', 'navy', '#07b', '#0096c7', '#00b4d880', 'rgba(0,119,182,0.8)'],

      inline: true,
      defaultColor: this.value || "#1f0202",
      swatches: [],
      onChange: (color, input) => {
        if (this.value === color) return;
        this.value = color;
        if (this.onColorChange && typeof this.onColorChange === "function") {
          Utilities.debounce(
            () => {
              const input = this.querySelector("#color_picker");
              this.onColorChange(this.value);
              input.dispatchEvent(new Event("input", { bubbles: true }));
            },
            50,
            "colorPicker",
            10,
          )();
        }
      },
    });
  }

  _renderContent() {
    return html`
      <div id="color_picker_content">
        <input id="color_picker" type="text" .value=${this.value} data-coloris style="display:none" />
      </div>
      <div class="_colorpicker_swatches">
        <div class="_colorpicker_swatches_header">
          <div class="_colorpicker_swatches_title">Global Colors</div>
          <button class="_colorpicker_add_swatch" @click="${this._addCurrentColorToSwatches}" title="Add current color to global colors" type="button">
            <i class="bx bx-plus"></i>
          </button>
        </div>
        <div class="_colorpicker_swatches_list">
          ${this.swatches.map(
            (color) => html`
              <div class="_colorpicker_swatch_item" style="background-color: ${color}" @click="${() => this._selectSwatchColor(color)}">
                <button class="_colorpicker_remove_swatch" @click="${(e) => this._removeSwatchColor(color, e)}" title="Remove color from global colors" type="button">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }
}

if (!customElements.get("rd-color-picker-panel")) customElements.define("rd-color-picker-panel", ColorPickerPanel);
